import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { buscarCabecalhoEmpresa, desenharCabecalhoPadrao, desenharRodapePaginacao } from "./relatorioCabecalho";

/**
 * Exporta um relatório em PDF com o cabeçalho padrão do sistema: logo (se a
 * empresa cadastrou um), nome da empresa, cidade/UF da fazenda ativa,
 * telefone, nome e período do relatório, data/hora de geração, e rodapé
 * "Página X de Y" em todas as páginas.
 *
 * O bug histórico daqui era o cabeçalho reservar espaço só na 1ª página
 * (via `startY`) — da 2ª em diante a tabela voltava a começar perto do
 * topo e sobrepunha a caixa do cabeçalho. Corrigido reservando o mesmo
 * espaço em toda página via `margin.top`, e desenhando o cabeçalho só em
 * `didDrawPage` (nunca antes do autoTable) — uma única fonte da verdade
 * pra a altura do cabeçalho, em vez de duas chamadas que podiam divergir.
 *
 * Relatório com mais de uma tabela (colunas diferentes entre elas, então não
 * dá pra misturar num `colunas`/`dados` só) usa `secoes` no lugar: uma tabela
 * por item da lista, cada uma com seu título desenhado acima e checagem de
 * espaço antes de desenhar — se não couber o título mais uma folga de
 * tabela na página atual, pula pra próxima e redesenha o cabeçalho padrão.
 *
 * @param {Object} opcoes
 * @param {string} opcoes.titulo - Nome do relatório.
 * @param {{key: string, label: string}[]} [opcoes.colunas] - Colunas, na ordem de exibição. Ignorado se `secoes` for passado.
 * @param {Object[]} [opcoes.dados] - Linhas (objetos com as chaves de `colunas`). Ignorado se `secoes` for passado.
 * @param {{titulo?: string, colunas: {key: string, label: string}[], dados: Object[], columnStyles?: Object, formatarValor?: Function}[]} [opcoes.secoes] - Várias tabelas no mesmo PDF, uma por seção. Seção com `dados` vazio é pulada.
 * @param {string} [opcoes.periodo] - Ex.: "01/01/2026 até 31/01/2026". Omitido se o relatório não tiver filtro de período.
 * @param {"landscape"|"portrait"} [opcoes.orientation="landscape"]
 * @param {Object} [opcoes.columnStyles] - Repassado direto pro `columnStyles` do jspdf-autotable. Ignorado se `secoes` for passado (cada seção tem o seu).
 * @param {(valor: any, coluna: {key: string, label: string}, item: Object) => string} [opcoes.formatarValor] - Ignorado se `secoes` for passado (cada seção tem o seu).
 */
export async function exportarRelatorioPDF({
  titulo,
  colunas,
  dados,
  secoes,
  periodo,
  orientation = "landscape",
  columnStyles,
  formatarValor = (valor) => (valor === null || valor === undefined ? "" : String(valor)),
}) {
  const listaSecoes = secoes || [{ colunas, dados, columnStyles, formatarValor }];
  const secoesComDados = listaSecoes.filter((s) => s.dados && s.dados.length > 0);
  if (secoesComDados.length === 0) {
    alert("⚠️ Não há dados para exportar!");
    return;
  }

  const empresa = await buscarCabecalhoEmpresa();

  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const margem = 10;

  const alturaCabecalho = periodo ? 40 : 34;
  const desenharCabecalho = () => desenharCabecalhoPadrao(doc, { titulo, periodo, empresa, largura, margem });

  const margemSuperior = 10 + alturaCabecalho + 6;
  const margin = { left: margem, right: margem, top: margemSuperior, bottom: 16 };

  let cursorY = margemSuperior;
  secoesComDados.forEach((secao) => {
    const colunasSecao = secao.colunas;
    const formatarValorSecao =
      secao.formatarValor || ((valor) => (valor === null || valor === undefined ? "" : String(valor)));
    const fontSize = colunasSecao.length > 9 ? 7 : colunasSecao.length > 6 ? 8 : 9;

    if (secao.titulo) {
      // Só o título mais uma folga de ~15mm — se não couber, a próxima
      // página já reserva o espaço do cabeçalho via margin.top.
      if (cursorY + 15 > altura - margin.bottom) {
        doc.addPage();
        desenharCabecalho();
        cursorY = margemSuperior;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(40);
      doc.text(secao.titulo, margem, cursorY + 4);
      cursorY += 8;
    }

    autoTable(doc, {
      startY: cursorY,
      margin,
      head: [colunasSecao.map((c) => c.label)],
      body: secao.dados.map((item) => colunasSecao.map((c) => formatarValorSecao(item[c.key], c, item))),
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize,
        cellPadding: 2,
        valign: "middle",
        lineWidth: 0.1,
        lineColor: [220, 220, 220],
        textColor: [40, 40, 40],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [26, 61, 42], // brand-green — mesma cor do resto do sistema
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0,
      },
      alternateRowStyles: { fillColor: [246, 248, 246] },
      columnStyles: secao.columnStyles,
      didDrawPage: desenharCabecalho,
    });

    cursorY = doc.lastAutoTable.finalY + 10;
  });

  desenharRodapePaginacao(doc, { largura, altura, margem });

  window.open(doc.output("bloburl"), "_blank");
}

/** Monta a string de período a partir de um filtro { data_inicio, data_fim } — ou `undefined` se nenhuma das duas foi preenchida. */
export function formatarPeriodoFiltro(dataInicio, dataFim) {
  if (!dataInicio && !dataFim) return undefined;
  const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR") : null);
  const inicio = fmt(dataInicio);
  const fim = fmt(dataFim);
  if (inicio && fim) return `${inicio} até ${fim}`;
  if (inicio) return `a partir de ${inicio}`;
  return `até ${fim}`;
}
